import { SnapshotFilters, DateRange, Timezone } from '@/features/report-snapshots/api/schema';
import { FilterBlock } from '@intercom/surge';
import { CalendarIcon } from '@intercom/surge/icons';
import { GlobeIcon } from '@intercom/surge/icons';
import { formatDateRangeWithYear } from '@/features/report-snapshots/formatters/date-range-formatter';
import { useLingui } from '@lingui/react/macro';

const DateRangeChip = ({ dateRange, timezone }: { dateRange: DateRange; timezone: Timezone }) => {
  const { i18n } = useLingui();
  const details = formatDateRangeWithYear(dateRange, timezone, i18n);

  return <FilterBlock icon={<CalendarIcon />} details={details} isClickable={false} />;
};

const formatTimezoneDisplay = (timezone: Timezone): string => {
  const formattedTimezone =
    new Intl.DateTimeFormat('en', {
      timeZone: timezone.ianaName,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value ?? '';

  // Extract readable city name from IANA identifier
  const cityName = timezone.ianaName.split('/').pop()?.replace(/_/g, ' ') ?? timezone.name;

  return `${cityName} time (${formattedTimezone})`;
};

// Helper function to render filters recursively, inserting the logical operator between items
const renderFilters = (filtersGroup: SnapshotFilters): React.ReactElement[] => {
  const elements: React.ReactElement[] = [];

  filtersGroup.filters.forEach((filter) => {
    if ('label' in filter) {
      // It's a SnapshotFilter
      elements.push(
        <FilterBlock
          key={`${filter.label}-${filter.operator}-${filter.relation}-${filter.values.join(',')}`}
          name={filter.label}
          details={`${filter.relation} ${filter.values.join(', ')}`}
          isClickable={false}
        />,
      );
    } else {
      // It's a nested SnapshotFilters - render recursively
      const nested = renderFilters(filter);
      elements.push(
        <span
          key={`group-${filter.type}-${JSON.stringify(filter.filters)}`}
          className="inline-flex flex-wrap items-center gap-2"
        >
          {nested}
        </span>,
      );
    }
  });

  return elements;
};

interface FilterBarProps {
  snapshotFilters?: SnapshotFilters | null;
  dateRange: DateRange;
  timezone: Timezone;
}

export const FilterBar = ({ snapshotFilters, dateRange, timezone }: FilterBarProps) => {
  const hasFilters = snapshotFilters?.filters && snapshotFilters.filters.length > 0;

  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-4 w-full">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <DateRangeChip dateRange={dateRange} timezone={timezone} />
        {hasFilters && (
          <>
            <span className="text-muted">|</span>
            {renderFilters(snapshotFilters)}
          </>
        )}
      </div>

      <div className="flex items-center gap-1 font-semibold whitespace-nowrap mt-1">
        <GlobeIcon className="h-4 w-4" />
        <span>{formatTimezoneDisplay(timezone)}</span>
      </div>
    </div>
  );
};
